import React, { useRef, useCallback, useState, useEffect, useMemo } from 'react';
import Turndown from 'turndown';
import { withIgnoreKeys } from 'react-hotkeys';
import he from 'he';

import { matchHotKey } from 'utils/doc-hotkeys';
import { toggleMd, getInputAfterTrigger, insertTextAtPosition, deserialize, SPECIAL_MENTIONS } from 'utils/editor';
import { setSelection, escapeRegExp } from 'utils/helpers';
import { createEmojiElement } from 'utils/html';
import { debugLog as log, logError } from 'shared-services/logger-service';
import { emojisRegex } from 'md/plugins/emoji';
import { escapeCode } from 'md/md-utils';

import MentionSuggestions from 'components/mention-suggestions';
import EditorHoverMenu from 'components/editor-hover-menu';
import MultiLineInput from 'components/MultiLineInput';

const encodeValue = value => {
    return he.encode(value, {
        useNamedReferences: true
    });
}

const MENTION_TRIGGER_CHAR = '@';

const turndown = new Turndown();
const turndownSimple = new Turndown();

function cleanAttribute (attribute) {
    return attribute ? attribute.replace(/(\n+\s*)+/g, '\n') : ''
}

const spanRule = {
    filter: 'span',
    replacement: function (content, element) {
        if (element.classList.contains('c-emoji')) {
            return content.match(/:.+?:/)[0] + ' ';
        }

        return content;
    }
}

const divRule = {
    filter: 'div',
    replacement: function (content) {
        return '\n' + content;
    }
}

const styleRule = {
    filter: 'style',
    replacement: () => ''
}

const inlineLinkRule = {
    filter: function (node, options) {
        return (
            options.linkStyle === 'inlined' &&
            node.nodeName === 'A' &&
            node.getAttribute('href')
        )
    },

    replacement: function (content, node) {
        var href = node.getAttribute('href')
        var title = cleanAttribute(node.getAttribute('title'))
        if (title) title = ' "' + title + '"'
        return '[' + content + '](' + href + ')'
    }
}

const headingRuleDisabled = {
    filter: ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'],
    replacement: function (content, node, options) {
        return '\n\n' + content + '\n\n';
    }
}

const focusAfterElement = element => {
    const textNode = element?.nextSibling;

    requestAnimationFrame(() => {
        if (textNode) {
            setSelection({
                node: textNode,
                offset: 1
            });
        } else if (element) {
            setSelection({
                node: element,
                offset: 1
            });
        }
    });
}

turndown.addRule('span', spanRule);
turndown.addRule('div', divRule);
turndown.addRule('style', styleRule);
turndown.addRule('inlineLink', inlineLinkRule);
turndown.addRule('heading', headingRuleDisabled);

turndownSimple.addRule('span', spanRule);
turndownSimple.addRule('div', divRule);
turndownSimple.addRule('style', styleRule);
turndownSimple.addRule('inlineLink', inlineLinkRule);
turndownSimple.addRule('heading', headingRuleDisabled);

const TEXT_NODE_TYPE = 3;
const FRAGMENT_NODE_TYPE = 11;

const enhaceForeignHTML = html => {
    return html.replaceAll('<span class="c-mrkdwn__br" data-stringify-type="paragraph-break"', '<br');
};

const getSelectionObject = () => {
    const selection = window.getSelection();
    const { anchorNode, focusNode, anchorOffset, focusOffset } = selection;

    return {
        anchorNodeIndex: anchorNode?.parentNode ? Array.from(anchorNode.parentNode.childNodes).indexOf(anchorNode) : 0,
        anchorOffset,
        focusNodeIndex: focusNode?.parentNode ? Array.from(focusNode.parentNode.childNodes).indexOf(focusNode) : 0,
        focusOffset
    };
};

const breakAnchor = () => {
    const selection = window.getSelection();
    const { focusNode, focusOffset } = selection;

    if (focusNode.nodeName === 'A') {
        const textNode = document.createTextNode('');
        focusNode.after(textNode);

        setSelection({
            node: textNode,
            offset: 0
        });
    } else if (focusNode.parentNode?.nodeName === 'A') {
        const { parentNode } = focusNode;
        const textNode = document.createTextNode(parentNode.textContent);
        parentNode.after(textNode);

        parentNode.parentNode.removeChild(parentNode);

        setSelection({
            node: textNode,
            offset: Math.min(focusOffset, textNode.textContent.length)
        });
    }
};

const createMentionElement = (id, text) => {
    const mention = document.createElement('span');
    mention.setAttribute('data-id', id);
    mention.setAttribute('data-type', 'user');
    mention.textContent = `@${text}`;
    mention.className = `mention`;
    mention.contentEditable = false;

    return mention;
};

const RichMDEditor = ({ placeholder, autofocus, value, mentionUsers, simpleMd, onKeyDown, onUpPress, onEsc, onReturn, onFilesPaste, onChange, onFocus, onBlur, provideController }) => {
    const inputCtrl = useRef(null);
    const menuCtrl = useRef(null);
    const element = useRef(null);
    const lastSelection = useRef(null);
    const [suggestionsOpen, setSuggestionsOpen] = useState(false);
    const [userMentionSearchTerm, setUserMentionSearchTerm] = useState(null);
    const [focused, setFocused] = useState(false);
    const [undoStack, setUndoStack] = useState([]);
    const [redoStack, setRedoStack] = useState([]);
    const [inputValue, setInputValue] = useState(value);

    const restoreSelection = useCallback(selection => {
        if (!selection) {
            return;
        }

        const inputElement = inputCtrl.current?.getElement();

        if (!inputElement) {
            return;
        }

        const anchorNode = inputElement.childNodes[selection.anchorNodeIndex];
        const focusNode = inputElement.childNodes[selection.focusNodeIndex];

        if (!anchorNode || !focusNode) {
            return;
        }

        setSelection({
            node: anchorNode,
            offset: selection.anchorOffset
        }, {
            node: focusNode,
            offset: selection.focusOffset
        });
    }, [inputCtrl]);

    const push = useCallback((toRedo, fromRedo) => {
        const state = {
            value: inputCtrl.current.getValue(true),
            selection: getSelectionObject()
        };

        if (toRedo) {
            setRedoStack(prevState => [...prevState, state]);
        } else {
            setUndoStack(prevState => [...prevState, state]);
            if (!fromRedo) {
                setRedoStack([]);
            }
        }

    }, [inputCtrl, setRedoStack, setUndoStack]);

    const applyHistoryItem = useCallback(state => {
        if (!state) {
            return;
        }

        inputCtrl.current?.setValue(state.value);
        restoreSelection(state.selection);
        setInputValue(state.value);

        menuCtrl.current?.update();
    }, [inputCtrl, menuCtrl, restoreSelection, setInputValue]);

    const undo = useCallback(() => {
        if (!undoStack.length) {
            return;
        }

        push(true);
        const state = undoStack[undoStack.length - 1];
        applyHistoryItem(state);

        setUndoStack(prevState => prevState.slice(0, prevState.length - 1));
    }, [push, undoStack, applyHistoryItem, setUndoStack]);

    const redo = useCallback(() => {
        push(false, true);

        const state = redoStack[redoStack.length - 1];
        applyHistoryItem(state);

        setRedoStack(prevState => prevState.slice(0, prevState.length - 1));
    }, [push, redoStack, applyHistoryItem, setRedoStack]);

    const insertText = useCallback((text, focus) => {
        const selection = window.getSelection();
        const { focusNode, focusOffset } = selection;
        const offset = focusNode.nodeType === TEXT_NODE_TYPE ? focusOffset : focusNode.textContent.length;

        focusNode.textContent = insertTextAtPosition(focusNode.textContent, text, offset);

        const node = focusNode.nodeType !== TEXT_NODE_TYPE ? focusNode.childNodes[0] : focusNode;

        if (focus) {
            try {
                setSelection({
                    node,
                    offset: Math.min(offset + text.length, node?.textContent.length || 0)
                });
            } catch (e) {
                logError('rich-md-editor', e);

                setSelection({
                    node,
                    offset: 1
                });
            }
        }

        menuCtrl.current?.update();
    }, []);

    const insertNewLine = useCallback(() => {
        if (inputCtrl.current?.isEmpty()) {
            return;
        }

        push();

        try {
            const selection = window.getSelection();

            if (!selection.isCollapsed) {
                document.execCommand('delete');
            }

            breakAnchor();
        } catch (e) {
            logError('rich-md-editor', e);
        }

        insertText('\n\u200B', true);

        setInputValue(inputCtrl.current.getValue(true));
    }, [insertText]);

    const insertElement = useCallback((element, withSpaceAfter, insertAfter) => {
        const textNode = document.createTextNode(' ');

        const selection = window.getSelection();
        const { focusNode, focusOffset } = selection;

        if (focusNode === inputCtrl.current.getElement()) {
            focusNode.appendChild(element);

            if (withSpaceAfter) {
                element.after(textNode);
            }

            inputCtrl.current?.focus(true);
        } else {
            const nodeContent = (focusNode.outerHTML || focusNode.textContent);
            const nodeContentBeforeSelection = nodeContent.slice(0, focusOffset);
            const nodeContentAfterSelection = nodeContent.slice(focusOffset);
            const insertAfterIndex = insertAfter && nodeContentBeforeSelection.lastIndexOf(insertAfter);
            const cleanNodeContentBeforeSelection = insertAfter ? nodeContentBeforeSelection.slice(0, insertAfterIndex) : nodeContentBeforeSelection;
            const lastChild = element.lastChild;

            focusNode.textContent = cleanNodeContentBeforeSelection;
            focusNode.after(element);

            if (withSpaceAfter) {
                element.after(textNode);

                textNode.after(nodeContentAfterSelection);

                setSelection({
                    node: textNode,
                    offset: 1
                });
            } else {
                if (element.nodeType === FRAGMENT_NODE_TYPE) {
                    if (lastChild) {
                        lastChild.after(nodeContentAfterSelection);

                        setSelection({
                            node: lastChild,
                            offset: lastChild.textContent.length
                        });
                    }
                } else {
                    element.after(nodeContentAfterSelection);

                    setSelection({
                        node: element,
                        offset: 1
                    });
                }
            }
        }


        log('[INPUT TEXT EDITOR] Focus from insert element');

        menuCtrl.current?.update();
    }, []);

    const insertMention = useCallback((id, text) => {
        push();

        const element = createMentionElement(id, text);

        insertElement(element, true, MENTION_TRIGGER_CHAR);

        setInputValue(inputCtrl.current.getValue(true));

        return element;
    }, []);

    const insertEmoji = useCallback((emoji, withFocus) => {
        push();

        if (withFocus) {
            restoreSelection(lastSelection.current);
        }

        insertElement(createEmojiElement(emoji), true);

        setInputValue(inputCtrl.current.getValue(true));
    }, [restoreSelection, lastSelection]);

    const toggleMdFn = useCallback(mdProps => {
        push();

        toggleMd(mdProps);

        menuCtrl.current?.update();

        setInputValue(inputCtrl.current.getValue(true));
    }, []);

    const toggleAction = useCallback(action => {
        switch (action) {
            case 'bold':
                toggleMdFn({ symbolBefore: '**', symbolAfter: '**' });
                return true;

            case 'italic':
                toggleMdFn({ symbolBefore: '_',symbolAfter: '_' });
                return true;

            case 'strikethrough':
                toggleMdFn({ symbolBefore: '~~', symbolAfter:'~~' });
                return true;

            case 'code':
                toggleMdFn({ symbolBefore: '`', symbolAfter: '`' });
                return true;

            case 'ordered-list':
                toggleMdFn({ symbolBefore: '1. ', fullLine: true, increment: true });
                return true;

            case 'unordered-list':
                toggleMdFn({ symbolBefore: '- ', fullLine: true });
                return true;

            case 'block-quote':
                toggleMdFn({ symbolBefore: '> ', fullLine: true });
                return true;

            case 'code-block':
                toggleMdFn({ symbolBefore: '```', symbolAfter: '```', fullLine: true, wrapBlock: true });
                return true;

            default:
                return false;
        }
    }, [toggleMdFn]);

    const getSerializedText = useCallback(readableFormat => {
        return inputValue
            .replace(/<span.*?class=(?:"|')mention(?:"|').*?>@.*?<\/span>/g, match => {
                const [, mentionId] = match.match(/<span.*?\sdata-id=(?:'|")(\S+?)(?:'|").*?>/);

                if (mentionId) {
                    if (readableFormat) {
                        const user = mentionUsers.find(u => u.id === mentionId);

                        if (user) {
                            return `@${user.name || user.email}`;
                        } else {
                            return `@${mentionId}`
                        }

                    }
                    return `<@${mentionId}>`;
                }

                return match;
            })
            .replace(/<span.*?class=(?:"|')emoji(?:"|').*?>.*?<\/span><\/span><\/span>/g, match => {
                const [, emojiId] = match.match(/<span.*?\sdata-id=(?:'|")(\S+?)(?:'|").*?>/);

                if (emojiId) {
                    return emojiId;
                }

                return match;
            })
            .replace(/<a href="(.+?)" target="_blank" rel="noopener noreferrer">(.+?)<\/a>/g, (full, url, text) => {
                return `[${text}](${url})`;
            });
    }, [inputValue]);

    const usersRegExp = useMemo(() => {
        const userNamesString = mentionUsers.map(({ name, email }) => name || email).filter(Boolean).map(escapeRegExp).join('|');
        return new RegExp(`(\\s|^)@(${SPECIAL_MENTIONS.join('|')}|${userNamesString})`, 'g');
    }, [mentionUsers]);

    const handleReturn = useCallback(event => {
        if (suggestionsOpen) {
            event.preventDefault();
            return;
        }

        if (onReturn && onReturn(event)) {
            return;
        }

        insertNewLine();
    }, [suggestionsOpen, insertNewLine, onReturn]);

    const handleUp = useCallback(event => {
        if (suggestionsOpen) {
            return;
        }

        onUpPress && onUpPress(event);
    }, [suggestionsOpen, onUpPress]);

    const handleKeyDown = useCallback(event => {
        onKeyDown && onKeyDown(event);

        const hotKey = matchHotKey(event);

        switch (hotKey.action) {
            case 'undo':
                event.preventDefault();
                undo();
                return;

            case 'redo':
                event.preventDefault();
                redo();
                return;

            default:
                // eslint
        }

        if (toggleAction(hotKey.type)) {
            event.preventDefault();
            return;
        }

        if (!Boolean(undoStack.length)) {
            push();
        }

        switch (event.which) {
            case 32: // Esc
                push();
                break;

            case 27: // Esc
                onEsc && onEsc();
                break;

            case 13: // Enter
                handleReturn(event);
                return;

            case 38: // Up
                handleUp(event);
                return;

            default:
                // eslint
        }
    }, [onKeyDown, undo, redo, Boolean(undoStack.length), handleReturn, handleUp, onEsc]);

    const convertToElements = useCallback((value = '', withSpaceAfter) => {
        return escapeCode(value, escapedSource => {
            return escapedSource
                .replace(usersRegExp, (fullMatch, spaceBefore, userName) => {
                    if (SPECIAL_MENTIONS.includes(userName)) {
                        return `${spaceBefore}${createMentionElement(userName, userName).outerHTML}${(withSpaceAfter ? ' ' : '')}`;
                    }

                    const user = mentionUsers.find(u => (u.name || u.email) === userName);

                    if (!user) {
                        return spaceBefore + '@' + userName;
                    }

                    return spaceBefore + createMentionElement(user.id, userName).outerHTML + (withSpaceAfter ? ' ' : '');
                })
                .replace(new RegExp(`(${emojisRegex})(\\s)`, 'g'), (match, emoji, emojiName, skinTone, spaceChar) => {
                    return `${createEmojiElement(emoji).outerHTML}${spaceChar || (withSpaceAfter ? ' ' : '')}`;
                })
                .replace(/\[([^\[]+?)\](\(.*?\)+)/g, link => {
                    return link.replace(/^\[(.+?)\]\(((?:\/|https?:\/\/)[\w\d.,/?=#()&-]+)\)$/, (full, text, url) => {
                        if (text && url) {
                            return `<a href="${url}" target="_blank" rel="noopener noreferrer">${text}</a>`;
                        }

                        return full;
                    });
                });
        });
    }, [usersRegExp, mentionUsers]);

    const handleChange = useCallback(value => {
        setUserMentionSearchTerm(getInputAfterTrigger(MENTION_TRIGGER_CHAR));

        const updatedValue = convertToElements(value, true);

        if (updatedValue !== value) {
            inputCtrl.current?.setValue(updatedValue);

            inputCtrl.current?.focus(true);
        }

        setInputValue(updatedValue);

        menuCtrl.current?.update();

        onChange && onChange();
    }, [convertToElements, setUserMentionSearchTerm, onChange]);

    const handleSelectionChange = useCallback(() => {
        setUserMentionSearchTerm(getInputAfterTrigger(MENTION_TRIGGER_CHAR));
        lastSelection.current = getSelectionObject();
    }, [setUserMentionSearchTerm]);

    const handleFocus = useCallback(() => {
        setFocused(true);
        onFocus && onFocus();
    }, [setFocused, onFocus])

    const handleBlur = useCallback(() => {
        setFocused(false);
        onBlur && onBlur(getSerializedText());
    }, [setFocused, getSerializedText, onBlur])

    const handlePaste = useCallback((event, value) => {
        const { nativeEvent } = event;
        event.persist();
        const clipboardData = nativeEvent.clipboardData || nativeEvent.originalEvent.clipboardData;

        if (onFilesPaste) {
            if (clipboardData.items && !clipboardData.getData('text/plain')) {
                try {
                    const items = Array.from(clipboardData.items);
                    const files = items.filter(({ kind }) => kind === 'file').map(item => item.getAsFile()).filter(Boolean);

                    if (files.length) {
                        onFilesPaste(files);
                        event.preventDefault();
                    }
                } catch(e) {
                    logError('rich-md-editor', e);
                }
            }
        }

        push();

        if (!window.getSelection().isCollapsed) {
            document.execCommand('delete');
        }

        const niftyMD = clipboardData.getData('text/nifty');

        let nextText;
        let withHTML = false;

        if (niftyMD) {
            nextText = deserialize(niftyMD);
            withHTML = true;
        } else {
            const html = clipboardData.getData('text/html');
            const parsedValue = html && (simpleMd ? turndownSimple : turndown).turndown(enhaceForeignHTML(html));

            if (parsedValue) {
                if (!/<.+?>/.test(parsedValue)) {
                    nextText = value;
                } else {
                    nextText = parsedValue;
                    withHTML = true;
                }
            } else {
                nextText = value;
            }

        }

        const convertedText = convertToElements(nextText);

        if (convertedText !== nextText) {
            withHTML = true;
        }

        breakAnchor();

        if (withHTML) {
            const fragment = document.createElement('template');
            fragment.innerHTML = convertedText;

            insertElement(fragment.content, false)
        } else {
            insertText(nextText, true);
        }

        setInputValue(inputCtrl.current.getValue(true));

        return true;
    }, [insertElement, convertToElements, onFilesPaste]);

    const handleCopy = useCallback(event => {
        event.clipboardData.setData('text/nifty', document.getSelection().getRangeAt(0).cloneContents().textContent);
        event.clipboardData.setData('text/plain', document.getSelection().getRangeAt(0).cloneContents().textContent);
        event.preventDefault();
    }, []);

    const handleMenuClick = useCallback(action => {
        toggleAction(action);
    }, [toggleAction]);

    const handleMentionSelect = useCallback((userId, text) => {
        const element = insertMention(userId, text);

        setUserMentionSearchTerm(null)

        focusAfterElement(element);
    }, [insertMention, setUserMentionSearchTerm])

    const handleSuggestionsShow = useCallback(() => {
        setSuggestionsOpen(true);
    }, [setSuggestionsOpen])

    const handleSuggestionsHide = useCallback(() => {
        setSuggestionsOpen(false);
    }, [setSuggestionsOpen]);

    const reset = useCallback(() => {
        inputCtrl.current?.reset();

        setInputValue('');
    }, [inputCtrl]);

    useEffect(() => {
        const ctrl = {
            focus: inputCtrl.current?.focus,
            blur: inputCtrl.current?.blur,
            reset,
            isEmpty: inputCtrl.current?.isEmpty,
            insertEmoji: emoji => insertEmoji(emoji, true),
            getValue: readableFormat => {
                return getSerializedText(readableFormat);
            }
        };

        if (provideController) {
            if ('current' in provideController) {
                provideController.current = ctrl;
            } else {
                provideController && provideController(ctrl);
            }
        }

        return () => {
            if (provideController) {
                if ('current' in provideController) {
                    provideController.current = null;
                } else {
                    provideController && provideController(null);
                }
            }
        }
    }, [getSerializedText, inputCtrl]);

    return (
        <div className="rich-md-editor" ref={element}>
            <MultiLineInput
                placeholder={placeholder}
                autoFocus={autofocus}
                canEdit={true}
                blurOnEnter={false}
                withEncode={false}
                spellcheck={true}
                value={convertToElements(encodeValue(value || ''))}
                onKeyDown={handleKeyDown}
                onChange={handleChange}
                onSelectionChange={handleSelectionChange}
                onFocus={handleFocus}
                onBlur={handleBlur}
                onPaste={handlePaste}
                onCopy={handleCopy}
                provideController={inputCtrl}
            />

            <EditorHoverMenu focused={focused} onClick={handleMenuClick} parentElement={element} provideController={menuCtrl} />

            <MentionSuggestions
                users={mentionUsers}
                term={userMentionSearchTerm}
                onSelect={handleMentionSelect}
                onShow={handleSuggestionsShow}
                onHide={handleSuggestionsHide}
            />
        </div>
    );
};

export default withIgnoreKeys(RichMDEditor);
